import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AuthInitializationRecovery } from "@/components/AuthInitializationRecovery";

/**
 * Lands the user after they click a magic link in their email.
 * Supabase's browser client picks the auth tokens out of the URL hash
 * automatically as part of `onAuthStateChange`, so we mostly just wait
 * for the session to materialize and then redirect to the requested internal path (or /dashboard).
 *
 * If the URL contains an `error_description` query / hash param (e.g.,
 * expired link), we surface it instead of silently redirecting.
 */
function safeInternalRedirect(value: string | null | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return "/dashboard";
  }

  return value;
}

export default function AuthCallback() {
  const { session, isLoading, initializationError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    // Magic-link errors come back in either the query string or the hash.
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const description = params.get("error_description") ?? hashParams.get("error_description");
    if (description) {
      setErrorMsg(decodeURIComponent(description));
    }
  }, []);

  // Once the auth listener has hydrated a session, send the user to the requested page.
  useEffect(() => {
    if (!isLoading && session && !errorMsg) {
      navigate(safeInternalRedirect(searchParams.get("redirect")), { replace: true });
    }
  }, [session, isLoading, errorMsg, navigate, searchParams]);

  if (errorMsg) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm space-y-4 rounded-lg border border-destructive/40 bg-destructive/5 p-5 text-sm text-destructive">
          <h2 className="text-base font-semibold">Sign-in link couldn't be used</h2>
          <p>{errorMsg}</p>
          <Navigate to="/login" replace />
        </div>
      </div>
    );
  }

  if (initializationError) {
    return <AuthInitializationRecovery />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <p className="text-sm text-muted-foreground">Signing you in…</p>
    </div>
  );
}
